import { InMemoryCache } from '@apollo/client/core';

/**
 * Build the Apollo `InMemoryCache` with our typePolicies.
 *
 * Pure module (no Angular DI, no environment/session imports) so the cache
 * identity rules are unit-testable — see `graphql-cache.spec.ts`, which pins
 * the audit-stamp snapshot behaviour. Consumed by `graphql.providers.ts`,
 * which owns the lazily-built singleton shared between the Apollo client
 * factory and the boot-time persistence hydrator.
 */
export function buildCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      // ── ID resolution for types that use _id instead of id ──
      // Apollo's default heuristic uses the `id` field. Every GraphQL
      // type that returns `_id` instead MUST have an explicit
      // `keyFields: ['_id']` here, otherwise Apollo will treat each
      // selection set as a separate cache entry — defeating de-duplication
      // and producing the dreaded "Cache data may be lost" warning when
      // two queries return different subsets of the same object.
      //
      // KEEP ALPHABETISED so additions are easy to spot in diffs.
      //
      // AUDIT STAMPS (createdBy / updatedBy) are per-row SNAPSHOTS, not
      // entities: their `_id` is the ACTOR's user id, so Apollo's default
      // `_id` normalisation would merge every stamp authored by the same
      // user into ONE shared cache object — each row's `timestamp` then
      // shows whichever row was fetched last (e.g. a just-created post
      // displaying "2 months ago" from the author's older article).
      // `keyFields: false` keeps each stamp embedded in its parent row.
      ArticleAuditStampType: { keyFields: false },
      ArticleCommentType: { keyFields: ['_id'] },
      ArticleType: {
        keyFields: ['_id'],
        fields: {
          // Stamp selections differ per query (some include `picture`);
          // merge instead of replace so a lighter selection can never
          // wipe fields a richer one already cached on the same article.
          createdBy: { merge: true },
          updatedBy: { merge: true },

          // ── The same rule, for the two embedded objects that were losing
          //    fields in production. Neither type has an id, so Apollo cannot
          //    normalise them and a narrower selection REPLACES the whole
          //    object rather than updating part of it.
          //
          // `media` is ContentMedia, and four different shapes are written to
          // it across the app: {image,video} from the nine card queries,
          // {orgPublished,image,video} from the home-preview feed,
          // {orgPublished,primaryKind,image,video} from the broadcast front
          // page, and {video} from the video-upload poll. On Discover the
          // narrowest lands LAST — feed, then front page, then spotlight — so
          // `orgPublished` was being dropped on every article and
          // `mapPublication` read it back as 'UNKNOWN'.
          //
          // `poll` is the sharper one. VOTE_POLL_MUTATION selects
          // `poll { totalVotes options { id label voteCount } }` and nothing
          // else, so casting a vote from the feed wrote a two-field poll over
          // the six-field one and wiped `question`, `allowMultiple`,
          // `closesAt` and `closed` from the cached article. Voting on a poll
          // deleted the question it was asking.
          media: { merge: true },
          poll: { merge: true },
        },
      },

      // The asset level, one deeper. ContentMediaAsset has no id either, and
      // `video` is written with nine fields by the feed and with `{posterUrl}`
      // alone by the card queries — so a merge on `media` above still leaves
      // the asset inside it being replaced.
      ContentMedia: {
        fields: {
          image: { merge: true },
          video: { merge: true },
          audio: { merge: true },
        },
      },

      // Studio's own content rows. ContentItem normalises on its `id`, but its
      // embedded media does not: the front-page picker omits `audio`, which
      // the studio table selects, so paging the picker dropped it.
      ContentItem: {
        fields: {
          media: { merge: true },
        },
      },
      Asset: { keyFields: ['_id'] },
      AssetType: { keyFields: ['_id'] },
      BookingType: { keyFields: ['_id'] },
      BuildingAlertAuditStampType: { keyFields: false },
      BuildingAlertType: { keyFields: ['_id'] },
      CateringMenuItemType: { keyFields: ['_id'] },
      CateringMenuPublicationType: { keyFields: ['_id'] },
      CateringMenuType: {
        keyFields: ['_id'],
        fields: {
          items: { merge: false }, // Always replace — items array changes on add/delete
        },
      },
      CateringOrderType: { keyFields: ['_id'] },
      CateringVendorType: { keyFields: ['_id'] },
      CompanyInfoType: { keyFields: false }, // singleton, no ID
      DigestType: { keyFields: ['_id'] },
      DivisionType: { keyFields: ['_id'] },
      FlowProjectType: { keyFields: ['_id'] },
      FlowSessionType: { keyFields: ['_id'] },
      IssueType: { keyFields: ['_id'] },
      KnowledgeAuditStampType: { keyFields: false },
      MeetingType: { keyFields: ['_id'] },
      PolicyProposalAuditStampType: { keyFields: false },
      // POLL OPTIONS ARE PER-POLL, NOT ENTITIES. Their `id` is the option's
      // slot inside its own poll — the composer writes `opt1`, `opt2`, and the
      // fixtures write `poll-open-3-opt-0` — so the same id sits in every poll
      // authored the same way. Apollo's default `id` normalisation merged all
      // of them into ONE `PollOptionType:opt1`, and every poll on the wall then
      // showed whichever poll's options were fetched last: "Try this poll!"
      // (Bread / Pasta, 6 votes to 3) rendered as Pizza / Burgers, 1 to 1, and
      // a vote on it moved the bars on a different post. Caught on screen,
      // 18 Sep. Embedding them in their poll is the only correct identity.
      PollOptionType:     { keyFields: false },
      // BOOKING SLOTS ARE PER-SPACE, PER-DAY, NOT ENTITIES. `AvailableSlotType.id`
      // is the tile's name in its space's grid — `am`, `pm`, `all-day` — so the
      // same three ids come back for every desk on every day, under both
      // `mapAvailability.spaces[].availableSlots` and
      // `bookingCapabilities.availableSlots`. Apollo's default `id`
      // normalisation kept ONE `AvailableSlotType:all-day` for the whole cache:
      // after a building's snapshot every desk's slots read back as the LAST
      // desk's, with its times. AutoBookService picks from that snapshot, so a
      // free desk read as taken (Home's Book space refused desk 01 at Rum Runner
      // with nothing sent, 4 Oct) and a taken one could read as free. Same shape
      // and same fix as poll options above: embed each slot in its space.
      AvailableSlotType:  { keyFields: false },
      QuickLinkType:      { keyFields: ['_id'] },
      RoleDefinitionType: { keyFields: ['_id'] },
      SpaceIssueType: { keyFields: ['_id'] },
      SpaceType: { keyFields: ['_id'] },
      UserQuickLinkType: { keyFields: ['_id'] },
      ZoneType: { keyFields: ['_id'] },

      // ── Merge nested objects without IDs ──
      // Prevents "Cache data may be lost" warnings when two queries
      // return different subsets of fields for the same parent object.
      // The schedule's room rows carry an `events` ARRAY whose length differs
      // per query — the day grid selects a full day, the room-detail read
      // selects a slice. Apollo has no id for ScheduleRoomType, so a narrower
      // selection REPLACES the array and warns "Cache data may be lost when
      // replacing the events field of a ScheduleRoomType object... This could
      // cause additional (usually avoidable) network requests to fetch data
      // that were otherwise cached" — which is exactly what it did, on a
      // surface that already re-fetches per day.
      //
      // The incoming list wins outright rather than being concatenated: two
      // reads of the same room and day are alternative ANSWERS, not pages, so
      // merging them would duplicate every block on the grid. Declaring it
      // silences the warning AND makes the replacement deliberate.
      ScheduleRoomType: {
        keyFields: false,
        fields: {
          events: {
            merge: (_existing: unknown, incoming: unknown) => incoming,
          },
        },
      },

      FloorType: {
        fields: {
          mapConfig: { merge: true },
        },
      },
      // ⚠ A PERMISSION THAT CAN BE DROPPED BY A NARROWER WRITE.
      //
      // `LiveEventType` normalises on `id` by Apollo's default heuristic, and
      // `LiveEventViewerCapabilitiesType` has no id of its own — so it is an
      // embedded object, and an embedded object is REPLACED wholesale by any
      // selection that writes it.
      //
      // `setLiveEventCrew` returned five of the six capability fields. Its
      // result therefore overwrote the cached block with one missing
      // `manageCrew`, and a producer who had just changed the crew watched the
      // crew controls disappear until they reloaded — the client deciding they
      // had lost a permission the server never took away.
      //
      // The selection is fixed too. This is the guard that means the next
      // partial selection cannot do it again: same reasoning as `media` and
      // `poll` on ArticleType above, but the field is an authorisation, so the
      // failure is a control vanishing rather than a picture.
      LiveEventType: {
        fields: {
          capabilities: { merge: true },
        },
      },
      LocationType: {
        fields: {
          address: { merge: true },
          mapConfig: { merge: true },
        },
      },

      // ── Query-level merge policies ──
      Query: {
        fields: {
          locationOccupancy: { merge: true },
          // Articles: replace on each fetch (paginated list)
          articles: { merge: false },
          // Quick links: always replace (no pagination)
          quickLinks: { merge: false },
          myQuickLinks: { merge: false },
          // Building alerts: replace — admin list is filterable.
          activeBuildingAlerts: { merge: false },
          buildingAlertList: { merge: false },
          buildingAlertsForTarget: { merge: false },
          buildingAlertsOverlappingWindow: { merge: false },
          // Catering orders: replace.
          myCateringOrders: { merge: false },
          cateringOrdersForBooking: { merge: false },
          // Paged issues feed.
          pagedIssues: { merge: false },
          // Divisions admin list.
          divisions: { merge: false },
        },
      },
    },
  });
}
